/* ==========================================================================
   Mini-cart custom element — Proffvolt
   Implements the interface product-form.js expects:
     - getSectionsToRender()
     - renderContents(parsedState)
     - setActiveElement(element)
     - open() / close() / toggle()
   Triggered by any element with [data-mini-cart-toggle] attribute
   ========================================================================== */

if (!customElements.get('mini-cart')) {
  class MiniCart extends HTMLElement {
    constructor() {
      super();
      this.activeElement = null;
      this.isOpen = false;

      // Close button(s)
      this.querySelectorAll('[data-mini-cart-close]').forEach((el) => {
        el.addEventListener('click', this.close.bind(this));
      });

      // Esc to close + Tab-trap inside dialog
      this.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') this.close();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab' || !this.isOpen) return;
        const focusable = this.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });

      // Global click: toggle from cart icon, close on outside click
      document.addEventListener('click', (e) => {
        const toggleEl = e.target.closest('[data-mini-cart-toggle]');
        if (toggleEl) {
          e.preventDefault();
          this.toggle();
          return;
        }
        if (this.isOpen && !e.target.closest('mini-cart')) {
          this.close();
        }
      });

      // Re-position only on resize. On scroll we deliberately do NOT reposition,
      // so the mini-cart stays pinned where it was opened (otherwise it jumps
      // whenever the sticky header hides/shows).
      this._reposition = this.positionNearCartIcon.bind(this);
      window.addEventListener('resize', this._reposition);

      // Bind row controls + discount form from initial server-rendered content
      this.bindRowControls();
      this.bindDiscountForm();

      // Listen for cart updates from anywhere else (e.g., cart page actions)
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (data) => {
          if (!data || data.source === 'mini-cart') return;
          // The product-form path will already call renderContents() directly.
          // Only run a passive refresh if this was fired from somewhere else.
          if (data.source !== 'product-form') {
            this.refreshFromServer();
          }
        });
      }
    }

    // ---------- Open / close / toggle -------------------------------------
    open() {
      this.positionNearCartIcon();
      this.removeAttribute('hidden');
      this.setAttribute('open', '');
      this.isOpen = true;
      document.documentElement.classList.add('mini-cart-open');
      // Focus the close button for a11y after animation frame
      requestAnimationFrame(() => {
        const closeBtn = this.querySelector('[data-mini-cart-close]');
        if (closeBtn) closeBtn.focus({ preventScroll: true });
      });
    }

    close() {
      this.removeAttribute('open');
      this.setAttribute('hidden', '');
      this.isOpen = false;
      document.documentElement.classList.remove('mini-cart-open');
      if (this.activeElement && typeof this.activeElement.focus === 'function') {
        this.activeElement.focus({ preventScroll: true });
      }
    }

    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    }

    // ---------- Position relative to cart icon ----------------------------
    positionNearCartIcon() {
      const cartIcon = document.getElementById('cart-icon-bubble');
      if (!cartIcon) return;
      const rect = cartIcon.getBoundingClientRect();
      // Top: below icon with a small gap
      const top = Math.max(10, rect.bottom + 10);
      // Right: distance from viewport right edge to right edge of icon
      const right = Math.max(8, window.innerWidth - rect.right);
      this.style.setProperty('--mini-cart-top', `${top}px`);
      this.style.setProperty('--mini-cart-right', `${right}px`);
    }

    // ---------- Sections API (consumed by product-form.js) -----------------
    setActiveElement(element) {
      this.activeElement = element;
    }

    getSectionsToRender() {
      return [
        { id: 'mini-cart', selector: '#mini-cart-content' },
        { id: 'cart-icon-bubble' },
      ];
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return parsed ? parsed.innerHTML : '';
    }

    renderContents(parsedState) {
      this.classList.remove('is-empty');
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
      this.rebindAfterRender();
      this.announceCartUpdate();
      setTimeout(() => this.open());
    }

    // Announce cart state to screen readers via the global live region
    announceCartUpdate() {
      const liveRegion = document.getElementById('lumen-sr-cart-live');
      if (!liveRegion) return;
      try {
        const bubble = document.querySelector('.cart-count-bubble span[aria-hidden]');
        const count = bubble ? parseInt(bubble.textContent, 10) || 0 : 0;
        const msg = count > 0
          ? `Produkt lagt i handlekurv. Totalt ${count} ${count === 1 ? 'vare' : 'varer'} i kurven.`
          : 'Handlekurven er oppdatert.';
        // Clear then set — forces re-announce if message is identical
        liveRegion.textContent = '';
        setTimeout(() => { liveRegion.textContent = msg; }, 50);
        setTimeout(() => { liveRegion.textContent = ''; }, 2500);
      } catch (e) { /* silent */ }
    }

    rebindAfterRender() {
      this.bindRowControls();
      this.bindDiscountForm();
      // Re-bind close button (it was re-rendered inside #mini-cart-content)
      this.querySelectorAll('[data-mini-cart-close]').forEach((el) => {
        el.addEventListener('click', this.close.bind(this));
      });
    }

    // ---------- Passive refresh (no add-to-cart) --------------------------
    async refreshFromServer() {
      try {
        const sectionIds = this.getSectionsToRender().map((s) => s.id).join(',');
        // POST to /cart/update.js (with empty updates) — returns sections and latest cart state
        const response = await fetch(`${window.routes.cart_update_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json;charset=UTF-8',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            updates: {},
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });
        if (!response.ok) throw new Error('Failed to fetch cart sections');
        const data = await response.json();
        if (data && data.sections) {
          this.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            if (!sectionElement || !data.sections[section.id]) return;
            sectionElement.innerHTML = this.getSectionInnerHTML(data.sections[section.id], section.selector);
          });
          this.rebindAfterRender();
        }
        return data;
      } catch (e) {
        console.error('mini-cart: refreshFromServer failed', e);
        return null;
      }
    }

    // ---------- Line-item controls ----------------------------------------
    bindRowControls() {
      this.querySelectorAll('[data-mini-cart-qty-increment]').forEach((btn) => {
        btn.addEventListener('click', () => this.stepQuantity(btn, +1));
      });
      this.querySelectorAll('[data-mini-cart-qty-decrement]').forEach((btn) => {
        btn.addEventListener('click', () => this.stepQuantity(btn, -1));
      });
      this.querySelectorAll('[data-mini-cart-qty-input]').forEach((input) => {
        input.addEventListener('change', () => {
          const row = input.closest('[data-line-index]');
          if (!row) return;
          const idx = parseInt(row.dataset.lineIndex, 10);
          const val = Math.max(0, parseInt(input.value, 10) || 0);
          this.changeLine(idx, val);
        });
      });
      this.querySelectorAll('[data-mini-cart-remove]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const row = btn.closest('[data-line-index]');
          if (!row) return;
          const idx = parseInt(row.dataset.lineIndex, 10);
          this.changeLine(idx, 0);
        });
      });
    }

    stepQuantity(btn, delta) {
      const row = btn.closest('[data-line-index]');
      if (!row) return;
      const idx = parseInt(row.dataset.lineIndex, 10);
      const input = row.querySelector('[data-mini-cart-qty-input]');
      const current = parseInt(input.value, 10) || 0;
      const next = Math.max(0, current + delta);
      this.changeLine(idx, next);
    }

    async changeLine(line, quantity) {
      this.classList.add('is-updating');
      try {
        const sectionIds = this.getSectionsToRender().map((s) => s.id).join(',');
        const response = await fetch(`${window.routes.cart_change_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json;charset=UTF-8',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            line,
            quantity,
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (data && data.sections) {
          this.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            if (!sectionElement || !data.sections[section.id]) return;
            sectionElement.innerHTML = this.getSectionInnerHTML(data.sections[section.id], section.selector);
          });
          this.rebindAfterRender();
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'mini-cart',
            cartData: data,
          });
        }
      } catch (e) {
        console.error('mini-cart: changeLine failed', e);
      } finally {
        this.classList.remove('is-updating');
      }
    }

    // ---------- Discount code ---------------------------------------------
    bindDiscountForm() {
      const form = this.querySelector('[data-mini-cart-discount-form]');
      if (form && !form.dataset.bound) {
        form.dataset.bound = '1';
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          const input = form.querySelector('[data-mini-cart-discount-input]');
          const code = (input && input.value) ? input.value.trim() : '';
          if (code) this.applyDiscount(code);
        });
      }
      this.querySelectorAll('[data-mini-cart-discount-remove]').forEach((btn) => {
        if (btn.dataset.bound) return;
        btn.dataset.bound = '1';
        btn.addEventListener('click', () => this.removeDiscount());
      });
    }

    showDiscountError(msg) {
      const el = this.querySelector('[data-mini-cart-discount-error]');
      if (el) {
        el.textContent = msg;
        el.removeAttribute('hidden');
      }
    }

    hideDiscountError() {
      const el = this.querySelector('[data-mini-cart-discount-error]');
      if (el) {
        el.setAttribute('hidden', '');
        el.textContent = '';
      }
    }

    async applyDiscount(code) {
      this.classList.add('is-updating');
      this.hideDiscountError();
      try {
        const sectionIds = this.getSectionsToRender().map((s) => s.id).join(',');
        const response = await fetch(`${window.routes.cart_update_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json;charset=UTF-8',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            discount: code,
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });
        if (!response.ok) throw new Error('Discount request failed: ' + response.status);
        const data = await response.json();

        const applied =
          (data.total_discount && data.total_discount > 0) ||
          (data.cart_level_discount_applications && data.cart_level_discount_applications.length > 0);

        // Re-render sections regardless (cart state may still have changed)
        if (data.sections) {
          this.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            if (!sectionElement || !data.sections[section.id]) return;
            sectionElement.innerHTML = this.getSectionInnerHTML(data.sections[section.id], section.selector);
          });
          this.rebindAfterRender();
        }

        if (!applied) {
          this.showDiscountError('Ugyldig rabattkode');
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mini-cart', cartData: data });
        }
      } catch (e) {
        console.error('mini-cart: applyDiscount failed', e);
        this.showDiscountError('Kunne ikke bruke rabattkode');
      } finally {
        this.classList.remove('is-updating');
      }
    }

    async removeDiscount() {
      this.classList.add('is-updating');
      try {
        const sectionIds = this.getSectionsToRender().map((s) => s.id).join(',');
        const response = await fetch(`${window.routes.cart_update_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json;charset=UTF-8',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            discount: '',
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });
        if (!response.ok) throw new Error('Remove discount failed: ' + response.status);
        const data = await response.json();
        if (data.sections) {
          this.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            if (!sectionElement || !data.sections[section.id]) return;
            sectionElement.innerHTML = this.getSectionInnerHTML(data.sections[section.id], section.selector);
          });
          this.rebindAfterRender();
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mini-cart', cartData: data });
        }
      } catch (e) {
        console.error('mini-cart: removeDiscount failed', e);
      } finally {
        this.classList.remove('is-updating');
      }
    }
  }

  customElements.define('mini-cart', MiniCart);
}
